import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import TableSearch from "../../../../base/components/Table/tableSearch";
import { useLocationQuery } from "../../../../base/hooks/useQueryString";
import { useHighlight } from "../../../../base/hooks/useHighlight";
import Icon from "../../../../base/components/Icon";
import { DateRangePicker } from "../../../../base/components/DateRangePicker";
import {
    DEFAULT_DATE_FORMAT_FOR_BACKEND,
    formatISODate,
    formatJSDate,
    formatJSDateToIso
} from "../../../../base/helpers/date";
import { GENDERS, OtherFeatureClientValues, OtherFeatureTypes } from "../../../../base/constants/shared";
import joinClassNames from "../../../../base/helpers/joinClassNames";
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle, PopoverBody, PopoverHeader,
    UncontrolledPopover,
    UncontrolledTooltip
} from "reactstrap";
import Checkbox from "../../../../base/components/Checkbox";
import Button from "../../../../base/components/Button";
import { BUTTON_COLORS } from "../../../../base/components/Button/appearance";
import isEqual from "lodash.isequal";
import { DateTime } from "luxon";
import { ReactComponent as viewHL7 } from "../../../../assets/images/viewHL7.svg";
import { ReactComponent as markVerified } from "../../../../assets/images/markVerified.svg";
import { ReactComponent as markCancelled } from "../../../../assets/images/markCancelled.svg";
import { ReactComponent as reprocessHL7 } from "../../../../assets/images/reprocessHL7.svg";
import { EllipsisWithTooltip } from "../../../../base/components/Table/components";
import TemplatePopup from "./TemplatePopup";
import { useService } from "../../../../base/hooks/useService";
import TemplateService from "../../../../services/TemplateService";
import DropdownWithSearch from "../../../../base/components/DropdownWithSearch";
import { useDebounce } from "../../../../base/hooks/useDebounce";
import { useLoading } from "../../../../base/hooks/useLoading";
import { DEFAULT_LIMIT_AND_OFFSET } from "../../../../services/BiomarkersService";
import UserResultsService from "../../../../services/UserResults";
import ConfirmPopup, { ConfirmPopupWithReason } from "../../../../base/components/ConfirmPopup";
import useSelectTemplate from "../../../../base/hooks/useSelectTemplate";
import { useNavigate } from "react-router-dom";
import { useUpdateTemplates } from "../../../../base/context/templates";
import {
    CATEGORY_LABELS,
    CUSTOMER_RESULTS_CATEGORIES,
    HEALF_ORG_ID,
    RETEST_STATUS_LABELS,
    RETEST_REQUEST_STATUSES,
    RETEST_STATUS_OPTIONS,
    RETEST_STATUS_CODES
} from "../constants";
import {
    DNA_CATEGORY,
    FOOD_INTOLERANCE_CATEGORY,
    CATEGORIES as CATEGORIES_ENUM,
    STATUS_IDS
} from "../../../../base/constants/samples";
import { KEY_USER } from "../../../../base/constants/storage";
import useConfig from "../../../../base/hooks/useConfig";
import StorageService from "../../../../services/StorageService";
import ToasterService from "../../../../services/ToastService";
import { isServiceEditable } from "../../../../base/helpers/adminRolePermissions";
import RetestOrderModal from "../../../../base/components/RetestOrderModal";

export const MIN_DATE_2022_ISO = DateTime.fromFormat("2022-01-01", DEFAULT_DATE_FORMAT_FOR_BACKEND).toJSDate();

export const STATUSES = ["", "New", "In progress", "Error", "Verified", "Cancelled", "Processed by initial lab", "Analysis Initiated", "Passed QA/QC", "Failed QA/QC", "Waiting for quiz", "Pending", "Pending approval"];

export const CATEGORIES_BY_SERVICE = {
    "Blood biomarkers": "Blood",
    "DNAm biomarkers": "DNA",
    "Food Intolerance": "Food Intolerance",
}

export const CATEGORIES = (serviceSettings) => {
    const categories = [""];
    for (const [key, value] of Object.entries(CATEGORIES_BY_SERVICE)) {
        if (serviceSettings[key]) {
            categories.push(value);
        }
    }
    return categories;
}

export const DEFAULT_STATUSES = [1, 2, 3, 5];
export const DEFAULT_CATEGORIES = [1, 2, 3];

export const TEMPLATE_TYPES = {
    exactInterval: 1,
    pastDay: 2,
    pastWeek: 3,
    pastMonth: 4,
    pastThreeMonths: 5,
    pastSixMonths: 6,
    pastTwelveMonths: 7,
    lastDaysCount: 8
};

export const DATE_RANGE_PRESETS = [
    {},
    {},
    { daysCount: 1, monthsCount: 0 },
    { daysCount: 7, monthsCount: 0 },
    { daysCount: 0, monthsCount: 1 },
    { daysCount: 0, monthsCount: 3 },
    { daysCount: 0, monthsCount: 6 },
    { daysCount: 0, monthsCount: 12 },
    {}
];

export const DATE_RANGE_PRESETS_TO_DISPLAY = [
    { label: "Past day", id: 2 },
    { label: "Past week", id: 3 },
    { label: "Past month", id: 4 },
    { label: "Past 3 months", id: 5 },
    { label: "Past 6 months", id: 6 },
    { label: "Past 12 months", id: 7 }
];

export const MANUAL_UPLOAD_PRODUCT = "Manual upload";

export const isUpdatedTemplate = endRangeDate => {
    return formatJSDate(endRangeDate, DEFAULT_DATE_FORMAT_FOR_BACKEND) === DateTime.now().toFormat(DEFAULT_DATE_FORMAT_FOR_BACKEND);
};

export const getCurrentTemplateType = (initialType, endRangeDate) => {
    if (initialType) return initialType;
    if (isUpdatedTemplate(endRangeDate)) {
        return TEMPLATE_TYPES.lastDaysCount;
    }
    return undefined;
};

export const SearchPlaceholder = () => <>No customer results found</>;

export const TableFilter = ({ data, updateData, label, placeholder, allData, withClear = false, isSingleSelect = false }) => {
    const [isOpen, updateIsOpen] = useState(false);

    const checkboxes = isSingleSelect && typeof allData[0] === 'object'
        ? allData
        : allData.reduce((acc, current, index) => {
            if (index || isSingleSelect) {
                acc.push({
                    id: index,
                    name: current
                });
            }
            return acc;
        }, []);

    const showPlaceholder = isSingleSelect ? !data : !data.length;

    const handleCheckboxChange = id => {
        if (isSingleSelect) {
            updateData(id);
            updateIsOpen(false);
            return;
        }
        updateData(prev => (prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]));
    };

    const handleClear = () => {
        updateData(isSingleSelect ? allData[0].id : []);
    };

    const displayText = isSingleSelect
        ? (checkboxes.find(item => item.id === data)?.name ?? placeholder)
        : (showPlaceholder ? placeholder : data.reduce((prevValue, currValue) => prevValue + (prevValue ? ", " : "") + allData[currValue], ""));

    return (
        <section className="tw-flex tw-flex-col tw-flex-grow-1 tw-flex-shrink-1 tw-mr-2">
            <label className="mb-2">{label}</label>
            <Dropdown
                isOpen={isOpen}
                toggle={() => updateIsOpen(prevState => !prevState)}
                className="cursor-pointer d-inline-block filter-dropdown result-filter-responsible"
            >
                <div className="gap-2 d-flex">
                    <DropdownToggle
                        className={joinClassNames("filter-toggle flex-grow-1", isOpen && "with-border")}
                        tag="section"
                    >
                        <span
                            className={joinClassNames("ms-2 me-1 pointer-events-none user-select-none", showPlaceholder && "text-secondary")}
                        >
                            {displayText}
                        </span>
                        <i
                            className={joinClassNames("mdi mdi-chevron-down pointer-events-none user-select-none", isOpen && "mdi-rotate-180")} />
                    </DropdownToggle>
                    {
                        withClear &&
                        !showPlaceholder &&
                        <button
                            type="button"
                                className="bg-transparent border-0 text-danger flex-shrink-1 me-0 ms-auto"
                            onClick={handleClear}
                        >
                            Clear
                        </button>
                    }
                </div>

                <DropdownMenu className="px-1 pb-1 filter-menu w-100">
                    <section className="mx-1 filter-options custom-scrollbar">
                        {checkboxes.map((status, index) => {
                            const isChecked = isSingleSelect ? data === status.id : data?.includes(status.id);
                            return (
                                <Checkbox
                                    id={status.id}
                                    text={status.name}
                                    value={isChecked}
                                    onChange={() => handleCheckboxChange(status.id)}
                                    key={index}
                                    className="my-2 ms-1"
                                />
                            );
                        })}
                    </section>
                </DropdownMenu>

            </Dropdown>
        </section>
    );
};

export const TableHeader = ({
    searchProvider,
    filterStatusProvider,
    filterCategoryProvider,
    dateRangeProviders = {},
    filterRetestRequestProvider,
}) => {
    /**
     * @type {TemplateService}
     */
    const templateService = useService(TemplateService);
    const [[dateOfBirthStartDate, dateOfBirthEndDate], updateBirthDate] = useState(dateRangeProviders.dateOfBirth.getValue());
    const [[activatedAtStartDate, activatedAtEndDate, activatedAtFilterType], updateActivationDate] = useState(
        dateRangeProviders.activatedAt.getValue()
    );
    const [[sampleAtStartDate, sampleAtEndDate, sampleAtFilterType], updateTestDate] = useState(dateRangeProviders.sampleAt.getValue());
    const [[resultAtStartDate, resultAtEndDate, resultAtFilterType], updateResultDate] = useState(dateRangeProviders.resultAt.getValue());
    const [[labReceivedAtStartDate, labReceivedAtEndDate, labReceivedAtFilterType], updateLabReceivedDate] = useState(
        dateRangeProviders.labReceivedAt.getValue()
    );

    const [isFilterOrCategoryChanged, updateIsFilterOrCategoryChanged] = useState(false);

    const updateTemplatesOnSidebar = useUpdateTemplates();

    const searchFromQuery = searchProvider.getValue();

    const [searchInput, updateSearchInput] = useState(searchFromQuery);

    const [templateSearch, updateTemplateSearch] = useState("");

    const [debouncedSearch] = useDebounce(templateSearch);

    const [isLoading, { registerPromise }] = useLoading(true);
    const [templates, updateTemplates] = useState([]);

    const [showDeletePopup, updateShowDeletePopup] = useState(false);

    const locationQuery = useLocationQuery();
    const { templateId, templateName, isPrivate } = locationQuery.getAll();

    const getTemplateQuery = useSelectTemplate();
    const navigate = useNavigate();

    const allStateValues = {
        dateOfBirthStartDate,
        dateOfBirthEndDate,
        activatedAtStartDate,
        activatedAtEndDate,
        activatedAtFilterType,
        sampleAtStartDate,
        sampleAtEndDate,
        sampleAtFilterType,
        resultAtStartDate,
        resultAtEndDate,
        resultAtFilterType,
        labReceivedAtStartDate,
        labReceivedAtEndDate,
        labReceivedAtFilterType
    };

    const currentStatusValue = filterStatusProvider.getValue();
    const initialStatusValue = currentStatusValue ? (typeof currentStatusValue === "object" ? currentStatusValue : [currentStatusValue]) : [];
    const [statuses, updateStatuses] = useState(initialStatusValue ?? []);

    const currentCategoryValue = filterCategoryProvider.getValue();
    const initialCategoryValue = currentCategoryValue ? (typeof currentCategoryValue === "object" ? currentCategoryValue : [currentCategoryValue]) : [];
    const [categories, updateCategories] = useState(initialCategoryValue ?? []);

    const dateRangeArrays = Object.values(dateRangeProviders);

    const initialRetestRequestValue = filterRetestRequestProvider.getValue();
    const [retestStatus, updateRetestStatus] = useState(initialRetestRequestValue ?? RETEST_STATUS_CODES.ALL);

    const [isOpenTemplatePopup, updateIsOpenTemplatePopup] = useState(false);

    const isInitialStatuses = isEqual(statuses.sort(), DEFAULT_STATUSES);
    const isAppliedStatuses = isEqual(initialStatusValue?.sort(), DEFAULT_STATUSES);

    const isInitialCategories = isEqual(categories.sort(), DEFAULT_CATEGORIES);
    const isAppliedCategories = isEqual(initialCategoryValue?.sort(), DEFAULT_CATEGORIES);

    const hasValuesToClear =
        dateRangeArrays.some(provider => !!provider.getValue().some(value => !!value)) || !isAppliedStatuses || !!searchProvider.getValue();

    const hasValuesToApply =
        Object.values(allStateValues).some(value => !!value) ||
        !isInitialStatuses ||
        !isAppliedStatuses ||
        !isInitialCategories ||
        !isAppliedCategories ||
        ((!!searchInput || !!searchProvider.getValue()) && searchInput !== searchProvider.getValue());

    const isCustomersResultEditable = isServiceEditable("Customers Result");

    const submitFilters = () => {
        dateRangeArrays.forEach(({ alias, setValue }) => {
            const dateRange = {
                [alias[0]]: allStateValues[alias[0]] ? formatJSDateToIso(allStateValues[alias[0]]) : undefined,
                [alias[1]]: allStateValues[alias[1]] ? formatJSDateToIso(allStateValues[alias[1]], !allStateValues[alias[2]]) : undefined
            };
            if (alias[2]) {
                dateRange[alias[2]] = allStateValues[alias[2]];
            }
            setValue(dateRange);
        });
        filterStatusProvider.setValue(statuses);
        filterCategoryProvider.setValue(categories);
        searchProvider.setValue(searchInput);
        filterRetestRequestProvider.setValue(retestStatus);
    };

    const selectTemplate = template => {
        navigate({
            search: getTemplateQuery(template)
        });
    };

    const clearFilters = () => {
        locationQuery.setNewSearch({ offset: 0, status: DEFAULT_STATUSES });
        updateSearchInput("");
    };

    const saveAsTemplate = ({ templateName, isPrivate }) => {
        updateIsFilterOrCategoryChanged(false);
        const activatedAtType = getCurrentTemplateType(activatedAtFilterType, activatedAtEndDate);
        const sampleAtType = getCurrentTemplateType(sampleAtFilterType, sampleAtEndDate);
        const labRecievedType = getCurrentTemplateType(labReceivedAtFilterType, labReceivedAtEndDate);
        const resultAtType = getCurrentTemplateType(resultAtFilterType, resultAtEndDate);

        const templateValues = {
            isPrivate: isPrivate,
            name: templateName,
            dateOfBirthStart: formatJSDateToIso(dateOfBirthStartDate),
            dateOfBirthEnd: formatJSDateToIso(dateOfBirthEndDate, true),

            activatedAtStartDate: formatJSDateToIso(activatedAtStartDate),
            activatedAtEndDate: formatJSDateToIso(activatedAtEndDate, !activatedAtType),
            activatedAtFilterType: activatedAtType,

            sampleAtStartDate: formatJSDateToIso(sampleAtStartDate),
            sampleAtEndDate: formatJSDateToIso(sampleAtEndDate, !sampleAtType),
            sampleAtFilterType: sampleAtType,

            labReceivedAtStartDate: formatJSDateToIso(labReceivedAtStartDate),
            labReceivedAtEndDate: formatJSDateToIso(labReceivedAtEndDate, !labRecievedType),
            labReceivedAtFilterType: labRecievedType,

            resultAtStartDate: formatJSDateToIso(resultAtStartDate),
            resultAtEndDate: formatJSDateToIso(resultAtEndDate, !resultAtType),
            resultAtFilterType: resultAtType,

            status: statuses.length ? statuses : undefined,
            category: categories.length ? categories : undefined,

            searchString: searchInput?.toString()
        };

        if (templateId) {
            templateService.updateTemplate(templateId, templateValues).then(data => {
                selectTemplate(data);
                getTemplates();
            });
            return;
        }



        templateService.createTemplate(templateValues).then(data => {
            updateTemplates(prevState => [data, ...prevState]);
            selectTemplate(data);
        });
    };

    const getTemplates = useCallback(() => {
        registerPromise(
            templateService
                .getTemplateList({
                    ...DEFAULT_LIMIT_AND_OFFSET,
                    search: debouncedSearch
                })
                .then(({ data }) => {
                    updateTemplates(data);
                })
        );
    }, [debouncedSearch]);

    const handleDelete = id => {
        templateService
            .deleteTemplate(id)
            .then(() => {
                updateTemplatesOnSidebar({ shouldUpdate: true });
                updateTemplates(prevState => {
                    return prevState.filter(template => id !== template.id);
                });
            })
            .then(() => {
                if (id === templateId) {
                    clearFilters();
                }
            });
    };

    const handleFavouriteChange = (id, isFavourite) => {
        templateService.setTemplateAsFavourite(id, { isFavourite: !isFavourite }).then(() => {
            updateTemplatesOnSidebar({ shouldUpdate: true });
            updateTemplates(prevState => {
                return prevState.map(({ id: templateId, isFavourite, ...rest }) => {
                    if (id === templateId) {
                        return {
                            id: templateId,
                            isFavourite: !isFavourite,
                            ...rest
                        };
                    }
                    return {
                        id: templateId,
                        isFavourite,
                        ...rest
                    };
                });
            });
        });
    };

    useEffect(() => {
        updateStatuses(initialStatusValue);
    }, [initialStatusValue.toString()]);

    useEffect(() => {
        updateCategories(initialCategoryValue);
    }, [initialCategoryValue.toString()]);

    useEffect(() => {
        updateRetestStatus(initialRetestRequestValue ?? RETEST_STATUS_CODES.ALL);
    }, [initialRetestRequestValue]);

    useEffect(() => {
        updateSearchInput(searchFromQuery);
    }, [searchFromQuery]);

    useEffect(() => {
        getTemplates();
    }, [getTemplates]);

    useEffect(() => {
        updateLabReceivedDate(dateRangeProviders.labReceivedAt.getValue());
        updateActivationDate(dateRangeProviders.activatedAt.getValue());
        updateTestDate(dateRangeProviders.sampleAt.getValue());
        updateResultDate(dateRangeProviders.resultAt.getValue());
        updateBirthDate(dateRangeProviders.dateOfBirth.getValue());
    }, [dateRangeProviders]);

    const { servicesSettings } = useConfig();

    const useScreenWidth = () => {
        const [screenWidth, setScreenWidth] = useState(window.innerWidth);

        useEffect(() => {
            const handleResize = () => setScreenWidth(window.innerWidth);
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, []);

        return screenWidth;
    };

    const screenWidth = useScreenWidth();

    return (
        <section className="py-4 d-flex flex-column customer-sticky-header">
            {showDeletePopup && (
                <ConfirmPopup
                    isOpen={!!showDeletePopup}
                    updateIsOpen={updateShowDeletePopup}
                    onSubmit={() => handleDelete(showDeletePopup)}
                    title="Delete"
                    description="Are you sure you want to delete the chosen template?"
                    submitBtnText="Delete"
                    className="upload-manually__popup"
                />
            )}

            <section className="gap-3 mb-3 d-flex align-items-end">
                <TableSearch
                    className="biomarkers-search results-search"
                    style={{ maxWidth: '400px', flexGrow: 1 }}
                    search={searchInput}
                    onSearch={data => {
                        updateSearchInput(data);
                        updateIsFilterOrCategoryChanged(true);
                    }}
                    placeholder="Search"
                />

                <section className="flex-wrap gap-2 d-flex align-items-end">
                    <Button color={BUTTON_COLORS.primary} onClick={submitFilters} className="text-nowrap"
                        disabled={!hasValuesToApply}>
                        Apply filters
                    </Button>
                    {isCustomersResultEditable && (
                        <Button
                            color={BUTTON_COLORS.primaryOutline}
                            onClick={() => updateIsOpenTemplatePopup(true)}
                            className="no-text-wrap"
                            disabled={templateId ? !isFilterOrCategoryChanged : !hasValuesToApply}
                        >
                            {templateId ? "Update template" : "Save as template"}
                        </Button>
                    )}
                    {hasValuesToClear && (
                        <Button
                            color={BUTTON_COLORS.transparent}
                            disabled={!hasValuesToClear}
                            onClick={clearFilters}
                            className="text-danger no-border text-nowrap"
                        >
                            Clear
                        </Button>
                    )}
                </section>
            </section>

            <section className="gap-3 mb-3 d-flex align-items-center">
                <div className="flex-grow-1">
                    <TableFilter
                        data={statuses}
                        allData={STATUSES}
                        placeholder="Select status"
                        label="Status"
                        updateData={data => {
                            updateIsFilterOrCategoryChanged(true);
                            updateStatuses(data);
                        }}
                    />
                </div>

                <DropdownWithSearch
                    containerClassName="d-flex flex-column align-items-start flex-grow-1"
                    labelClassName="mb-2"
                    label="Templates"
                    placeholder="Select template"
                    onSearch={search => {
                        if (templateId) {
                            selectTemplate({});
                        }
                        updateTemplateSearch(search);
                    }}
                    search={templateSearch}
                    value={templateId && { id: templateId, name: templateName }}
                    items={templates}
                    isLoading={isLoading}
                    onSelect={data => {
                        updateIsFilterOrCategoryChanged(false);
                        selectTemplate(data);
                    }}
                    onDelete={id => updateShowDeletePopup(id)}
                    onFavourite={handleFavouriteChange}
                    ignoreClassNames
                />

                {CATEGORIES(servicesSettings).some(category => category !== "") && (
                    <div className="flex-grow-1">
                        <TableFilter
                            data={categories}
                            allData={CATEGORIES(servicesSettings)}
                            placeholder="Select Category"
                            label="Category"
                            updateData={data => {
                                updateIsFilterOrCategoryChanged(true);
                                updateCategories(data);
                            }}
                            withClear
                        />
                    </div>
                )}

                {screenWidth < 1600 && (
                    <div className="flex-grow-1">
                        <TableFilter
                            data={retestStatus}
                            allData={Object.entries(RETEST_STATUS_OPTIONS).map(([code, label]) => ({ id: Number(code), name: label }))}
                            placeholder={RETEST_STATUS_OPTIONS[RETEST_STATUS_CODES.ALL]}
                            label="Retest Status"
                            updateData={data => {
                                updateRetestStatus(data);
                                updateIsFilterOrCategoryChanged(true);
                            }}
                            isSingleSelect
                        />
                    </div>
                )}
            </section>

            <section className="filters-grid">
                <DateRangePicker
                    label="Birth date"
                    className="me-4 "
                    startDate={dateOfBirthStartDate}
                    yearCount={100}
                    endDate={dateOfBirthEndDate}
                    onChange={([startDate, endDate]) => {
                        updateIsFilterOrCategoryChanged(true);
                        updateBirthDate([startDate, endDate]);
                    }}
                />

                <DateRangePicker
                    label="Activation date"
                    className="me-4 "
                    startDate={activatedAtStartDate}
                    endDate={activatedAtEndDate}
                    onChange={([startDate, endDate, presetId]) => {
                        updateActivationDate([startDate, endDate, presetId]);
                        updateIsFilterOrCategoryChanged(true);
                    }}
                    minDate={MIN_DATE_2022_ISO}
                    withPresets
                    currentPreset={activatedAtFilterType}
                />

                <DateRangePicker
                    label="Sample date"
                    className="me-4 "
                    startDate={sampleAtStartDate}
                    endDate={sampleAtEndDate}
                    onChange={([startDate, endDate, presetId]) => {
                        updateIsFilterOrCategoryChanged(true);
                        updateTestDate([startDate, endDate, presetId]);
                    }}
                    minDate={MIN_DATE_2022_ISO}
                    withPresets
                    currentPreset={sampleAtFilterType}
                />

                <DateRangePicker
                    label="Result date"
                    className="me-4 "
                    startDate={resultAtStartDate}
                    endDate={resultAtEndDate}
                    onChange={([startDate, endDate, presetId]) => {
                        updateResultDate([startDate, endDate, presetId]);
                        updateIsFilterOrCategoryChanged(true);
                    }}
                    minDate={MIN_DATE_2022_ISO}
                    withPresets
                    currentPreset={resultAtFilterType}
                />

                <DateRangePicker
                    label="Lab received date"
                    startDate={labReceivedAtStartDate}
                    endDate={labReceivedAtEndDate}
                    onChange={([startDate, endDate, presetId]) => {
                        updateIsFilterOrCategoryChanged(true);
                        updateLabReceivedDate([startDate, endDate, presetId]);
                    }}
                    minDate={MIN_DATE_2022_ISO}
                    withPresets
                    currentPreset={labReceivedAtFilterType}
                />

                {screenWidth >= 1600 && (
                    <TableFilter
                        data={retestStatus}
                        allData={Object.entries(RETEST_STATUS_OPTIONS).map(([code, label]) => ({ id: Number(code), name: label }))}
                        placeholder={RETEST_STATUS_OPTIONS[RETEST_STATUS_CODES.ALL]}
                        label="Retest Status"
                        updateData={data => {
                            updateRetestStatus(data);
                            updateIsFilterOrCategoryChanged(true);
                        }}
                        isSingleSelect
                    />
                )}

            </section>


            {isOpenTemplatePopup && (
                <TemplatePopup
                    isOpen={isOpenTemplatePopup}
                    updateIsOpen={() => updateIsOpenTemplatePopup(false)}
                    initialTemplateName={templateName}
                    isInitialPrivate={isPrivate}
                    onSubmit={saveAsTemplate}
                />
            )}
        </section>
    );
};

export const RESULT_STATUSES = [
    {},
    { label: "New", className: "status-badge green-badge" },
    { label: "In progress", className: "status-badge orange-badge" },
    { label: "Error", className: "status-badge red-badge" },
    { label: "Verified", className: "status-badge blue-badge" },
    { label: "Cancelled", className: "status-badge grey-badge" },
    { label: "Processed by lab", className: "status-badge orange-badge" },
    { label: "Analysis Initiated", className: "status-badge orange-badge" },
    { label: "Passed QA/QC", className: "status-badge orange-badge" },
    { label: "Failed QA/QC", className: "status-badge red-badge" },
    { label: "Waiting for quiz", className: "status-badge orange-badge" },
    { label: "Pending", className: "status-badge orange-badge" },
    { label: "Pending approval", className: "status-badge blue-badge" }
];

export const CYCLE_PHASE_OPTIONS = [
    { value: OtherFeatureTypes.follicular, label: "Follicular" },
    { value: OtherFeatureTypes.ovulation, label: "Ovulatory" },
    { value: OtherFeatureTypes.luteal, label: "Luteal" },
    { value: OtherFeatureTypes.menopause, label: "Menopause" },
    { value: OtherFeatureTypes.postmenopause, label: "Postmenopause" }
];

export const ThreeDotDropdown = ({ isOpen, updateIsOpen, options, onSelect }) => {
    return (
        <Dropdown isOpen={isOpen} toggle={() => updateIsOpen(prevState => !prevState)}
            className="d-inline-block filter-dropdown ms-2">
            <DropdownToggle
                className={joinClassNames("btn btn-transparent no-border no-outline w-fit-content p-0", isOpen && "")}
                tag="button">
                <Icon icon="threeDots" className="" />
            </DropdownToggle>
            <DropdownMenu end className="three-dot-dropdown w-150 ">
                {options.map(({ label, icon, type, id, disabled }) => {
                    return (
                        <DropdownItem disabled={disabled === true} className="three-dot-dropdown__item w-100" key={id}
                            onClick={() => onSelect(type)}>
                            <Icon icon={icon} className="me-2 three-dot-dropdown__icon" />
                            <p className="mb-0">{label}</p>
                        </DropdownItem>
                    );
                })}
            </DropdownMenu>
        </Dropdown>
    );
};

const FurtherInformation = ({ result }) => {

    if (
        ![STATUS_IDS.inProgress, STATUS_IDS.processedByInitialLab].includes(result.status) ||
        result.category !== 2
    ) return null;

    const keyDate = {
        [STATUS_IDS.inProgress]: "labReceivedAt",
        [STATUS_IDS.processedByInitialLab]: "processedByInitialLabAt"
    };

    const formatDate = formatISODate(result[keyDate[result.status]]);

    const labelsTooltip = {
        [STATUS_IDS.inProgress]: `Received by the Lab: ${formatDate}`,
        [STATUS_IDS.processedByInitialLab]: `Lab results are ready, waiting for 
    Clock Foundation to analyse: ${formatDate}`
    };

    const iconTooltip = {
        [STATUS_IDS.inProgress]: "flask",
        [STATUS_IDS.processedByInitialLab]: "search"
    };

    return <div>
        <Icon icon={iconTooltip[result.status]} className="ms-2" id={`inProgressMessage-${result.id}`} />
        <UncontrolledTooltip
            popperClassName={joinClassNames("tooltip-alternative-name error-result-tooltip")}
            innerClassName="pre-line text-truncate error-inner-max-height"
            placement="bottom"
            target={`inProgressMessage-${result.id}`}
        >
            {labelsTooltip[result.status]}
        </UncontrolledTooltip>
    </div>;
};

const CYCLE_PHASE_COLUMN_WIDTH = 100;

const CyclePhaseCell = ({ value, row, onCyclePhaseChanged }) => {
    if (!value || row?.original?.sex === 1) {
        return "-";
    }

    /**
     * @type {UserResultsService}
     */
    const userResultsService = useService(UserResultsService);
    /**
     * @type {ToasterService}
     */
    const toastService = useService(ToasterService);

    const [editing, setEditing] = useState(false);
    const [selected, setSelected] = useState(value);
    const [isChangeCyclePhaseModalOpen, setIsChangeCyclePhaseModalOpen] = useState(false);
    const selectRef = useRef();

    const handleSave = (newValue) => {
        if (row?.original?.userOtherFeature !== newValue) {
            setSelected(newValue);
            setEditing(false);
            setIsChangeCyclePhaseModalOpen(true);
        } else {
            setSelected(newValue);
            setEditing(false);
        }
    };

    useEffect(() => {
        if (editing && selectRef.current) {
            selectRef.current.focus();
        }
    }, [editing]);

    return (
        <section className="d-flex align-items-center">
            {!editing ? (
                <div
                    style={{
                        width: CYCLE_PHASE_COLUMN_WIDTH,
                        minWidth: CYCLE_PHASE_COLUMN_WIDTH,
                        maxWidth: CYCLE_PHASE_COLUMN_WIDTH,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    <div
                        className="cursor-pointer w-100 text-truncate"
                        onClick={event => {
                            event.stopPropagation();
                            setEditing(true);
                        }}
                        style={{ flex: 1, minWidth: 0 }}
                    >
                        {OtherFeatureClientValues[selected]}
                    </div>
                    <span
                        id={`cycle-phase-edit-icon-${row?.original?.id}`}
                        className="cursor-pointer w-fit-content"
                        onClick={e => {
                            e.stopPropagation();
                            setEditing(true);
                        }}
                    >
                        <Icon
                            icon="edit"
                            width={16}
                            height={16}
                            className="cycle_phase_edit_icon ms-2"
                        />
                    </span>
                    <UncontrolledTooltip
                        placement="top"
                        target={`cycle-phase-edit-icon-${row?.original?.id}`}
                    >
                        Change Cycle Phase
                    </UncontrolledTooltip>
                    {isChangeCyclePhaseModalOpen && (
                        <ConfirmPopupWithReason
                            isOpen={isChangeCyclePhaseModalOpen}
                            updateIsOpen={(value) => {
                                setIsChangeCyclePhaseModalOpen(value);
                            }}
                            onSubmit={async (values) => {
                                try {
                                    await userResultsService.changeCyclePhase(
                                        row?.original?.id,
                                        {   
                                            cyclePhase: selected,
                                            reason: values.changeReason
                                        }
                                    );
                                    toastService.success("Cycle Phase changed successfully");
                                    if (typeof onCyclePhaseChanged === "function") {
                                        onCyclePhaseChanged(selected, row);
                                    }
                                    setIsChangeCyclePhaseModalOpen(false);
                                } catch (e) {
                                    setSelected(row?.original?.userOtherFeature);
                                    toastService.error("500 internal server error");
                                }
                            }}
                            title="Change Cycle Phase"
                            description="Changing the cycle status will delete previous recommendations and trigger reprocessing. Do you want to proceed?"
                            submitBtnText="Confirm"
                            labelText="Reason for change:"
                            className=""
                            payloadText="changeReason"
                            validationText="Change Reason is required."
                        />
                    )}
                </div>
            ) : (
                <div
                    style={{
                        width: CYCLE_PHASE_COLUMN_WIDTH,
                        minWidth: CYCLE_PHASE_COLUMN_WIDTH,
                        maxWidth: CYCLE_PHASE_COLUMN_WIDTH
                    }}
                >
                    <select
                        ref={selectRef}
                        className="form-select form-select-sm ms-0"
                        value={selected}
                        style={{ minWidth: "100%", width: "100%" }}
                        onChange={e => handleSave(Number(e.target.value))}
                        onBlur={() => setEditing(false)}
                        onClick={e => e.stopPropagation()}
                    >
                        {CYCLE_PHASE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            )}
        </section>
    );
};

export const THREE_DOT_TYPES = {
    reprocess: "REPROCESS",
    markAsVerified: "MARK_AS_VERIFIED",
    markAsCancelled: "MARK_AS_CANCELLED",
    view: "VIEW",
    viewResults: "VIEW_RESULTS",
    viewCancellationReason: "VIEW_CANCELLATION_REASON",
    downloadFile: "DOWNLOAD_FILE",
    markAsReceivedByLab: "MARK_AS_RECEIVED_BY_LAB",
    uploadCSVFile: "UPLOAD_CSV_FILE",
    viewCSVFile: "VIEW_CSV_FILE",
    reprocessCSVFile: "REPROCESS_CSV_FILE",
    downloadPDFFile: "DOWNLOAD_PDF_FILE",
    uploadManually: "UPLOAD_MANUALLY",
    markAsRejected: "MARK_AS_REJECTED",
    downloadJSON: "DOWNLOAD_JSON",
    resendToAPMS: "RESEND_TO_APMS",
};

export const VIEW_HL7 = { id: 1, label: "View Kit Object file", icon: "viewHL7", type: THREE_DOT_TYPES.view };
export const MARK_AS_CANCELLED = {
    id: 2,
    label: "Mark as cancelled",
    icon: "markCancelled",
    type: THREE_DOT_TYPES.markAsCancelled
};
export const MARK_AS_VERIFIED = {
    id: 3,
    label: "Mark as verified",
    icon: "markVerified",
    type: THREE_DOT_TYPES.markAsVerified
};
export const REPROCESS_KIT_OBJECT = {
    id: 4,
    label: "Reprocess Kit Object file",
    icon: "reprocessHL7",
    type: THREE_DOT_TYPES.reprocess
};
export const VIEW_RESULTS = { id: 5, label: "View results", icon: "viewResults", type: THREE_DOT_TYPES.viewResults };
export const VIEW_CANCELLATION_REASON = {
    id: 6,
    label: "View cancellation reason",
    icon: "viewResults",
    type: THREE_DOT_TYPES.viewCancellationReason
};
export const DOWNLOAD_FILE = {
    id: 9,
    label: "Download PDF file",
    icon: "download",
    type: THREE_DOT_TYPES.downloadFile
};

export const MARK_AS_RECEIVED_BY_LAB = {
    id: 10,
    label: "Mark as received by the lab",
    icon: "check",
    type: THREE_DOT_TYPES.markAsReceivedByLab
};

export const UPLOAD_CSV_FILE = {
    id: 11,
    label: "Upload CSV results file",
    icon: "download",
    type: THREE_DOT_TYPES.uploadCSVFile
};

export const VIEW_CSV_FILE = { id: 12, label: "View CSV file", icon: "viewHL7", type: THREE_DOT_TYPES.viewCSVFile };

export const REPROCESS_CSV_FILE = {
    id: 13,
    label: "Re-upload and reprocess CSV file",
    icon: "reprocessHL7",
    type: THREE_DOT_TYPES.reprocessCSVFile
};
export const DOWNLOAD_JSON = {
    id: 14,
    label: "Download JSON",
    icon: "downloadJSON",
    type: THREE_DOT_TYPES.downloadJSON,
};
export const RESEND_TO_APMS = {
    id: 15,
    label: "Resend to APMS",
    icon: "reprocessHL7",
    type: THREE_DOT_TYPES.resendToAPMS
}

export const BUTTONS_FOR_STATUSES = [
    [],
    [VIEW_HL7, MARK_AS_CANCELLED],
    [VIEW_HL7],
    [VIEW_HL7, MARK_AS_CANCELLED, MARK_AS_VERIFIED, REPROCESS_KIT_OBJECT],
    [VIEW_HL7, VIEW_RESULTS, DOWNLOAD_JSON, RESEND_TO_APMS],
    [VIEW_HL7, VIEW_CANCELLATION_REASON],
    [],
    [],
    [],
    []
];

export const BUTTONS_FOR_MANUAL_RESULTS = [VIEW_RESULTS, DOWNLOAD_JSON, RESEND_TO_APMS];

export const BUTTONS_FOR_FOOD_INTOLERANCE_STATUSES = [
    [],
    [MARK_AS_RECEIVED_BY_LAB, MARK_AS_CANCELLED],
    [UPLOAD_CSV_FILE, MARK_AS_CANCELLED],
    [VIEW_CSV_FILE, MARK_AS_CANCELLED, MARK_AS_VERIFIED, REPROCESS_CSV_FILE],
    [VIEW_CSV_FILE, VIEW_RESULTS, DOWNLOAD_FILE],
    [VIEW_CSV_FILE, VIEW_CANCELLATION_REASON],
    []
];

export const BUTTONS_FOR_DNA_STATUSES = [
    [],
    [MARK_AS_CANCELLED],
    [MARK_AS_CANCELLED],
    [MARK_AS_CANCELLED, REPROCESS_KIT_OBJECT],
    [VIEW_CSV_FILE, VIEW_RESULTS],
    [],
    [MARK_AS_CANCELLED],
    [MARK_AS_CANCELLED],
    [MARK_AS_CANCELLED],
    [MARK_AS_CANCELLED]
];

export const columns = [
    {
        Header: "#",
        width: 45,
        className: "first-sticky-column",
        headerClassName: "first-sticky-column",
        Cell: ({ row: { index } }) => {
            const { params: { offset = 0 } } = useLocationQuery();
            return <label className="mb-0">{index + 1 + (offset ?? 0)}</label>;
        }
    },
    {
        Header: "Customer name",
        accessor: "name",
        className: "second-sticky-column",
        headerClassName: "second-sticky-column",
        width: 168,
        canSort: true,
        Cell: ({
            row: {
                id,
                original: { firstName, lastName }
            }
        }) => {
            const { params } = useLocationQuery();
            const { decorateText } = useHighlight(params.search);
            const divRef = useRef();
            const value = firstName + " " + lastName;
            return (
                <EllipsisWithTooltip fullText={value} id={`name-${id}`} divRef={divRef}>
                    <div {...decorateText(value)} className="w-100 text-truncate" ref={divRef} />
                </EllipsisWithTooltip>
            );
        }
    },
    {
        Header: "Email",
        accessor: "email",
        width: 240,
        Cell: ({ value, row: { id } }) => {
            const { params } = useLocationQuery();
            const { decorateText } = useHighlight(params.search);
            const divRef = useRef();
            return (
                <EllipsisWithTooltip fullText={value} id={`email-${id}`} divRef={divRef}>
                    <div {...decorateText(value)} className="w-100 text-truncate" ref={divRef} />
                </EllipsisWithTooltip>
            );
        }
    },
    {
        Header: "Gender",
        accessor: "sex",
        width: 96,
        Cell: ({ value }) => {
            return <div className="w-100 text-truncate">{GENDERS[value]}</div>;
        }
    },
    {
        Header: "Birth date",
        accessor: "dateOfBirth",
        width: 124,
        Cell: ({ value }) => {
            return formatISODate(value);
        }
    },
    {
        Header: "Category",
        accessor: "category",
        width: 180,
        Cell: ({ value, row: { original } }) => {
            // -- if sampleCode is null -> claim this result as manually uploaded PDF result --
            if (!original?.sampleCode) {
                value = CUSTOMER_RESULTS_CATEGORIES.BLOOD_CATEGORY;
            }
            return <div className="w-100 text-truncate">{CATEGORY_LABELS[value]}</div>;
        }
    },
    {
        Header: "Sample ID",
        accessor: "sampleCode",
        width: 124,
        Cell: ({ value }) => {
            // -- if value(sampleCode) is null -> claim this result as manually uploaded PDF result --
            if (!value) {
                return "-";
            }
            const { params } = useLocationQuery();
            const { decorateText } = useHighlight(params.search);
            return <div {...decorateText(value)} className="w-100 text-truncate" />;
        }
    },
    {
        Header: "Cycle Phase",
        accessor: "userOtherFeature",
        width: CYCLE_PHASE_COLUMN_WIDTH,
        Cell: (props) => <CyclePhaseCell {...props} onCyclePhaseChanged={props.onCyclePhaseChanged} />
    },
    {
        Header: "Activ. date",
        accessor: "activatedAt",
        width: 124,
        canSort: true,
        Cell: ({ value }) => {
            return formatISODate(value);
        }
    },
    {
        Header: "Sample date",
        accessor: "sampleAt",
        width: 135,
        canSort: true,
        Cell: ({ value }) => {
            return formatISODate(value);
        }
    },
    {
        Header: "Lab rec. date",
        accessor: "labReceivedAt",
        width: 135,
        Cell: ({ value }) => {
            return formatISODate(value);
        }
    },
    {
        Header: "Result date",
        accessor: "resultAt",
        width: 124,
        canSort: true,
        Cell: ({ value }) => {
            return formatISODate(value);
        }
    },
    {
        Header: "Test ID",
        accessor: "id",
        width: 100,
        Cell: ({ value, row: { id } }) => {
            const divRef = useRef();
            return (
                <EllipsisWithTooltip fullText={value} id={`test-id-${id}`} divRef={divRef}>
                    <div className="w-100 text-truncate" ref={divRef}>
                        {value || "-"}
                    </div>
                </EllipsisWithTooltip>
            );
        }
    },
    {
        Header: "Product name",
        accessor: "testProductName",
        width: 180,
        Cell: ({ value, row: { id } }) => {
            const divRef = useRef();
            return (
                <EllipsisWithTooltip fullText={value} id={`testProductName-${id}`} divRef={divRef}>
                    <div className="w-100 text-truncate" ref={divRef}>
                        {value || "-"}
                    </div>
                </EllipsisWithTooltip>
            );
        }
    },
    {
        Header: "Order ID",
        accessor: "orderId",
        width: 100,
        Cell: ({ value, row: { id } }) => {
            const divRef = useRef();
            const { params } = useLocationQuery();
            const { decorateText } = useHighlight(params.search);
            return (
                <EllipsisWithTooltip fullText={value} id={`orderId-${id}`} divRef={divRef}>
                    <div {...decorateText(value || "-")} className="w-100 text-truncate" ref={divRef} />
                </EllipsisWithTooltip>
            );
        }
    },
    {
        Header: "Lab name",
        accessor: "lab",
        width: 124,
        Cell: ({ value, row: { id } }) => {
            const divRef = useRef();
            const { params } = useLocationQuery();
            const { decorateText } = useHighlight(params.search);
            return (
                <EllipsisWithTooltip fullText={value} id={`labName-${id}`} divRef={divRef}>
                    <div {...decorateText(value || "-")} className="w-100 text-truncate" ref={divRef} />
                </EllipsisWithTooltip>
            );
        }
    },
    {
        Header: "Lab ID",
        accessor: "labId",
        width: 100,
        Cell: ({ value, row: { id } }) => {
            const divRef = useRef();
            const { params } = useLocationQuery();
            const { decorateText } = useHighlight(params.search);
            return (
                <EllipsisWithTooltip fullText={value} id={`labId-${id}`} divRef={divRef}>
                    <div {...decorateText(value || "-")} className="w-100 text-truncate" ref={divRef} />
                </EllipsisWithTooltip>
            );
        }
    },
    {
        Header: "Retest",
        accessor: "retest",
        width: 180,
        Cell: ({
            row: {
                original: { category, status, retestRequest, failedTests, ...rowOriginal }
            },
            onRetestChanged
        }) => {
            const [isRetestModalOpen, setRetestModalOpen] = useState(false);

            const handleCloseModal = (newStatus) => {
                setRetestModalOpen(false);
                if (newStatus && typeof onRetestChanged === "function") {
                    onRetestChanged();
                }
            };

            if (category === CUSTOMER_RESULTS_CATEGORIES.BLOOD_CATEGORY &&
                status === STATUS_IDS.verified &&
                failedTests && failedTests !== "") {
                let color, icon, label;
                if (!retestRequest || Object.keys(retestRequest).length === 0) {
                    color = BUTTON_COLORS.light;
                    icon = "plus";
                    label = RETEST_STATUS_LABELS[RETEST_REQUEST_STATUSES.CREATE];
                } else if (retestRequest.status === RETEST_REQUEST_STATUSES.PENDING) {
                    color = BUTTON_COLORS.warning;
                    icon = "practitionersWaitlist";
                    label = RETEST_STATUS_LABELS[RETEST_REQUEST_STATUSES.PENDING];
                } else if (retestRequest.status === RETEST_REQUEST_STATUSES.ACTIVATED) {
                    color = BUTTON_COLORS.primary;
                    icon = "practitionersApprove";
                    label = RETEST_STATUS_LABELS[RETEST_REQUEST_STATUSES.ACTIVATED];
                } else if (retestRequest.status === RETEST_REQUEST_STATUSES.CANCELLED) {
                    color = BUTTON_COLORS.secondary;
                    icon = "close";
                    label = RETEST_STATUS_LABELS[RETEST_REQUEST_STATUSES.CANCELLED];
                }
                return (
                    <>
                        <Button
                            color={color}
                            className='px-2 py-1 d-flex text-nowrap align-items-center'
                            onClick={() => setRetestModalOpen(true)}
                        >
                            <Icon icon={icon} className="me-2" width={16} height={16} />
                            {label}
                        </Button>
                        <RetestOrderModal
                            isOpen={isRetestModalOpen}
                            onClose={handleCloseModal}
                            result={{
                                ...rowOriginal,
                                retestRequest: retestRequest || null
                            }}
                        />
                    </>
                );
            }
            return <span>-</span>;
        }
    },
    {
        Header: "Status",
        accessor: "status",
        className: "before-last-sticky-column",
        headerClassName: "before-last-sticky-column",
        width: 95,
        Cell: ({
            value,
            row: {
                original: { isCriticalResult, toFollow, id, errorNotifications, ...rest }
            }
        }) => {
            const { label, className } = RESULT_STATUSES[value] || { label: "-", className: "status-badge grey-badge" };
            const isErrorStatus = value === STATUS_IDS.error;
            let errorMessage = errorNotifications?.[errorNotifications.length - 1]?.message || toFollow;

            let errorsCount = 0;
            if (errorMessage && errorMessage.length) {
                errorMessage = errorMessage.split("\n");
                errorMessage = errorMessage.filter((error) => error.length > 0);
                errorsCount = errorMessage.length;
            }
            return (
                <section className="d-flex align-items-center">
                    <div className={joinClassNames("text-truncate", className)}>{label}</div>
                    {isCriticalResult && !isErrorStatus && <Icon icon="isCriticalResult" className="ms-2" />}
                    {isErrorStatus && (
                        <>
                            <Icon icon="infoCircle" className="cursor-pointer ms-2" id={`resultErrorMessage-${id}`} />
                            <UncontrolledPopover
                                trigger="focus"
                                popperClassName={joinClassNames("errors-popover")}
                                placement="bottom"
                                target={`resultErrorMessage-${id}`}
                            >
                                <PopoverHeader>
                                    Total errors: {errorsCount}
                                </PopoverHeader>
                                <PopoverBody>
                                    <ul className="px-3">
                                        {errorMessage && errorMessage.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </PopoverBody>
                            </UncontrolledPopover>
                        </>
                    )}
                    {<FurtherInformation result={{ isCriticalResult, toFollow, id, ...rest }} />}
                </section>
            );
        }
    },
    {
        Header: () => <div className="">Actions</div>,
        accessor: "actions",
        className: "last-sticky-column",
        headerClassName: "last-sticky-column",
        width: 75,
        Cell: ({
            row: {
                original: { id, status, category, testProductName, ...rest }
            },
            actions
        }) => {
            const [isOpen, updateIsOpen] = useState(false);

            const {
                markAsCancelled,
                markAsVerified,
                viewFiles,
                viewResults,
                reprocessHL7,
                viewCancellationReason,
                markAsReceivedByLab,
                uploadCSVFile,
                viewCSVFile,
                reprocessCSVFile,
                downloadFile,
                downloadJSON,
                resendToAPMS,
            } = actions;

            const menuActions = {
                [THREE_DOT_TYPES.view]: () => viewFiles({ id, status, ...rest }),
                [THREE_DOT_TYPES.markAsCancelled]: () => markAsCancelled(id),
                [THREE_DOT_TYPES.markAsVerified]: () => markAsVerified(id),
                [THREE_DOT_TYPES.reprocess]: () => reprocessHL7({ id, ...rest }),
                [THREE_DOT_TYPES.viewResults]: () => viewResults({ id, status, category, testProductName, ...rest }),
                [THREE_DOT_TYPES.viewCancellationReason]: () => viewCancellationReason(rest.cancellationReason),
                [THREE_DOT_TYPES.markAsReceivedByLab]: () => markAsReceivedByLab(id),
                [THREE_DOT_TYPES.uploadCSVFile]: () => uploadCSVFile({ id, status, category, ...rest }),
                [THREE_DOT_TYPES.viewCSVFile]: () => viewCSVFile({ status, id, category, ...rest }),
                [THREE_DOT_TYPES.reprocessCSVFile]: () => reprocessCSVFile({ id, status, category, ...rest }),
                [THREE_DOT_TYPES.downloadFile]: () => downloadFile(rest?.pdfResultFile?.link),
                [THREE_DOT_TYPES.downloadJSON]: () => downloadJSON({ id }),
                [THREE_DOT_TYPES.resendToAPMS]: () => resendToAPMS({ id, ...rest })
            };

            let options = [];

            // -- options for Food Intolerance --
            if (CATEGORIES_ENUM[category] === FOOD_INTOLERANCE_CATEGORY) {
                options = BUTTONS_FOR_FOOD_INTOLERANCE_STATUSES[status];
            }

            if (CATEGORIES_ENUM[category] === DNA_CATEGORY) {
                options = BUTTONS_FOR_DNA_STATUSES[status];
            }

            // -- options for Manually uploaded PDF Results --
            if (!rest?.sampleCode) {
                options = [
                    { ...DOWNLOAD_FILE, disabled: rest?.pdfResultFile?.link === undefined },
                    VIEW_RESULTS
                ];
            }

            // --  options for HL7 Results --
            if (!options.length) {
                options = BUTTONS_FOR_STATUSES[status];
            }

            if (testProductName === MANUAL_UPLOAD_PRODUCT) {
                options = BUTTONS_FOR_MANUAL_RESULTS;
            }

            const storage = useService(StorageService);
            const DEFAULT_OPTIMALLY_ME_ORG = 1;
            const { organisationId } = storage.get(KEY_USER, { organisationId: DEFAULT_OPTIMALLY_ME_ORG });
            if (organisationId !== HEALF_ORG_ID) {
                options = options.filter(opt => (opt?.type || opt?.type) !== THREE_DOT_TYPES.resendToAPMS);
            }

            return (
                <section
                    className={joinClassNames("d-flex align-items-center justify-content-center text-primary", isOpen && "force-z-index")}>
                    <ThreeDotDropdown
                        id={id}
                        isOpen={isOpen}
                        updateIsOpen={updateIsOpen}
                        options={options}
                        onSelect={type => {
                            menuActions[type]();
                        }}
                    />
                </section>
            );
        }
    }
];

export function getFilteredColumns() {
    const isCustomersResultEditable = isServiceEditable("Customers Result");

    if (isCustomersResultEditable) {
        return columns;
    }

    return columns
        .filter(col => col.accessor !== "actions")
        .map(col => {
            if (col.accessor === "status") {
                return {
                    ...col,
                    className: 'last-sticky-column',
                    headerClassName: 'last-sticky-column',
                };
            }
            return col;
        });
}
